import React, { MouseEvent, useContext } from 'react';
import { BudgetExpenseOrIncome } from 'business/constants';
import { useSelector } from 'store/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackEvent } from 'helpers/trackerHelpers';
import TabulatedPage from 'ui/TabulatedPage/TabulatedPage';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useGetScopes } from 'routes/BudgetModule/shared/useGetScopes';
import { useGetUserScopes } from 'routes/BudgetModule/shared/useGetUserScopes';
import ExpenseIncome from '../ExpenseIncome/ExpenseIncome';
import Analyse from '../Analyse/Analyse';
import {
    useArgumentComponent,
    useArgumentPathValue,
    getArgumentsListPathValue,
} from '../hooks/navigation';
import Projection from '../Projection/Projection';
import { tagFromProposal } from '../../helpers/eventHelpers';
import GridApiProvider from 'routes/BudgetModule/contexts/GridApiProvider';
import { CurrentBudgetProposalContext } from 'routes/BudgetModule/components/BudgetProposal/Resource/contexts/CurrentBudgetProposalContext';

const DEFAUL_PROJECTION_TYPE = 'atterrissage';

const StyTabulatedPage = styled(TabulatedPage, {
    name: 'StyTabulatedPage',
})({
    '& > div': {
        // TODO: improve this hardcoded value (height of tabs + header)
        height: 'calc(100vh - 117px)',
        overflowY: 'auto',
    },
    '& .Mui-TabulatedPage-content-div': { backgroundColor: '#EFEFEF' },
});

const ResourceContent = ({
    resourceName,
}: {
    resourceName: string;
}): React.ReactElement | null => {
    const { t } = useTranslation();
    const {
        budgetProposalId,
        viewBy,
        resourceSoftwareId = '',
        projectionType = DEFAUL_PROJECTION_TYPE,
    } = useParams();
    const navigate = useNavigate();
    const currentBudgetP = useContext(CurrentBudgetProposalContext);

    const userScopes = useGetUserScopes();
    const scopes = useGetScopes();
    const commonPath = `/budget_proposal/${budgetProposalId}/resource/${encodeURIComponent(
        resourceSoftwareId,
    )}`;
    const { isPdfModeActivated } = useSelector((state) => state.vision);

    const argumentTabPath = useArgumentPathValue(
        commonPath,
        isPdfModeActivated,
    );
    const argumentTabComponent = useArgumentComponent(resourceName);
    if (!currentBudgetP) return null;

    const handleArgumentTabClick = (e: MouseEvent) => {
        e.preventDefault();
        trackEvent(
            'Service Proposal: click tab argument',
            tagFromProposal(currentBudgetP),
        );
        navigate(getArgumentsListPathValue(commonPath));
    };
    const argumentTab = {
        label: t(`budget.arguments`),
        children: argumentTabComponent,
        pathname: argumentTabPath,
        disabled: false,
        onClick: handleArgumentTabClick,
    };

    const projectionTab = {
        label: t(`budget.projection`),
        children: (
            <GridApiProvider>
                <Projection
                    budgetProposal={currentBudgetP}
                    resourceSoftwareId={resourceSoftwareId}
                    resourceName={resourceName}
                />
            </GridApiProvider>
        ),
        pathname: `${commonPath}/projection/${projectionType}`,
        disabled: false,
    };

    const expenseIncomeTabs = [
        BudgetExpenseOrIncome.EXPENSE,
        BudgetExpenseOrIncome.INCOME,
    ].map((direction) => {
        const loweredDirection = direction.toLowerCase();
        return {
            label: t(`budget.${loweredDirection}`),
            children: (
                <ExpenseIncome
                    budgetProposal={currentBudgetP}
                    resourceName={resourceName}
                    expenseOrIncome={direction}
                />
            ),
            pathname: `${commonPath}/${loweredDirection}/${
                viewBy || 'imputations'
            }`,
            disabled: false,
            onClick: () =>
                trackEvent(
                    'Service Proposal: click tab expense',
                    tagFromProposal(currentBudgetP),
                ),
        };
    });

    const analysisTab = {
        label: t(`budget.analyze`),
        children: (
            <Analyse
                budgetProposal={currentBudgetP}
                resourceName={resourceName}
                resourceSoftwareId={resourceSoftwareId}
                userScopes={userScopes}
                scopes={scopes}
            />
        ),
        pathname: `${commonPath}/analyze`,
        disabled: false,
    };

    const tabsList = [
        ...expenseIncomeTabs,
        analysisTab,
        argumentTab,
        projectionTab,
    ];
    return (
        <Box sx={{ width: '100%' }}>
            <StyTabulatedPage
                tabsList={tabsList}
                // WARNING: here we want to keepSearch because in pdf mode we usually have a token in the url
                // which is considered as a search parameter, if we don't keep it, the tab selected will not
                // be selected correctly
                keepSearch
            />
        </Box>
    );
};

export default ResourceContent;
